import React from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { IconButton, styled, TableCell, TableRow, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { indexNames } from "constants/reports";
import { IAssetClass } from "store/types";

const StyledTableRowCell = styled(TableCell)({
  fontSize: "14px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  borderBottom: "1px solid #E5E9EB"
});

const StyledText = styled(Typography)({
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  textAlign: "center",
  fontSize: "14px",
  fontWeight: 400
});

interface IndexesTableRowProps {
  row: IAssetClass;
  expandedRows: number[];
  handleExpandRow: (rowId: number) => void;
  isChild: boolean;
  updateCellValue: (data: {
    anchorEl: HTMLTableCellElement;
    assetClass: IAssetClass;
    column: "private_index" | "public_index" | "expected_return";
  }) => void;
}

export const IndexesTableRow: React.FC<IndexesTableRowProps> = ({
  row,
  expandedRows,
  handleExpandRow,
  isChild,
  updateCellValue
}) => {
  const publicIndex =
    row.public_index && indexNames[row.public_index] ? indexNames[row.public_index] : row.public_index;

  return (
    <>
      <TableRow sx={{ height: "48px" }}>
        <StyledTableRowCell sx={{ width: 50 }}>
          {Boolean(row.sub_asset_class?.length) && (
            <IconButton
              onClick={() => handleExpandRow(row.id)}
              sx={{
                transform: expandedRows.includes(row.id) ? "rotate(180deg)" : "none",
                transition: "transform 0.2s"
              }}
            >
              <ArrowDropDownIcon sx={{ fill: "#071F35" }} />
            </IconButton>
          )}
        </StyledTableRowCell>
        <StyledTableRowCell sx={{ maxWidth: 120 }}>
          {!isChild && (
            <MaterialTooltip title={row.name} isShow>
              <StyledText sx={{ textAlign: "left", fontWeight: 700, color: "#071F35" }}>
                {row.name} {Boolean(row.sub_asset_class?.length) && `(${row.sub_asset_class?.length})`}
              </StyledText>
            </MaterialTooltip>
          )}
        </StyledTableRowCell>
        <StyledTableRowCell sx={{ maxWidth: 120 }}>
          {isChild && (
            <MaterialTooltip title={row.name} isShow>
              <StyledText sx={{ textAlign: "left", color: "#071F35" }}>{row.name}</StyledText>
            </MaterialTooltip>
          )}
        </StyledTableRowCell>
        <StyledTableRowCell
          onClick={(e) => updateCellValue({ anchorEl: e.currentTarget, assetClass: row, column: "private_index" })}
          sx={{
            maxWidth: 120,
            cursor: "pointer",
            "&:hover": { backgroundColor: "#ECF1F8" }
          }}
          tabIndex={0}
        >
          <MaterialTooltip title={row.private_index?.name || ""} isShow>
            <StyledText sx={{ textAlign: "left", color: "#26A6AE" }}>{row.private_index?.name || ""}</StyledText>
          </MaterialTooltip>
        </StyledTableRowCell>
        <StyledTableRowCell
          onClick={(e) => updateCellValue({ anchorEl: e.currentTarget, assetClass: row, column: "public_index" })}
          sx={{
            maxWidth: 120,
            cursor: "pointer",
            "&:hover": { backgroundColor: "#ECF1F8" }
          }}
          tabIndex={0}
        >
          <MaterialTooltip title={publicIndex || ""} isShow>
            <StyledText sx={{ textAlign: "left", color: "#26A6AE" }}>{publicIndex}</StyledText>
          </MaterialTooltip>
        </StyledTableRowCell>
        <StyledTableRowCell
          onClick={(e) => updateCellValue({ anchorEl: e.currentTarget, assetClass: row, column: "expected_return" })}
          sx={{
            maxWidth: 120,
            cursor: "pointer",
            "&:hover": { backgroundColor: "#ECF1F8" }
          }}
          tabIndex={0}
        >
          <MaterialTooltip title={typeof row.expected_return === "number" ? `${row.expected_return}%` : ""} isShow>
            <StyledText sx={{ textAlign: "left", color: "#26A6AE" }}>
              {typeof row.expected_return === "number" ? `${row.expected_return}%` : ""}
            </StyledText>
          </MaterialTooltip>
        </StyledTableRowCell>
      </TableRow>
      {expandedRows.includes(row.id) &&
        row.sub_asset_class?.map((child) => (
          <IndexesTableRow
            key={child.id}
            row={child}
            expandedRows={expandedRows}
            handleExpandRow={handleExpandRow}
            isChild={true}
            updateCellValue={updateCellValue}
          />
        ))}
    </>
  );
};
